import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MapPin,
  CreditCard as Edit2,
  Trash2,
  Plus,
  Monitor,
  Search,
  X,
  Link2,
  Filter,
} from 'lucide-react';
import { apiClient } from '../lib/apiClient';
import { logAudit, AUDIT_ACTIONS, AUDIT_ENTITIES, computeChanges } from '../lib/auditLog';
import { JobSiteDevices } from './JobSiteDevices';
import { JobSiteInspectionHistory } from './JobSiteInspectionHistory';
import { ConfirmModal, type ErrorContext } from './ConfirmModal';
import { JobSiteFormModal } from './JobSiteFormModal';
import { DataTable, type ColumnDef } from './DataTable';
import type { GPSLocation } from '../lib/googleMaps';

interface JobSite {
  id: string;
  name: string;
  address: string | null;
  contact_name: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  gps_location: GPSLocation | null;
  notes: string | null;
  aspire_property_id: number | null;
  active: boolean;
  created_at: string;
}

type ViewMode = 'list' | 'detail';
type SortField = 'name' | 'address' | 'created_at';

interface JobSitesManagementProps {
  /**
   * View-only mode — the Manager property tab. The list, the Details form, the
   * Devices tab and the Inspection History tab all render without a single
   * add / edit / delete affordance. This is presentation only: the real gate is
   * the Admin-only write role on /api/job-sites.
   */
  readOnly?: boolean;
}

export function JobSitesManagement({ readOnly = false }: JobSitesManagementProps) {
  const { t } = useTranslation('common');
  const [allJobSites, setAllJobSites] = useState<JobSite[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [detailTab, setDetailTab] = useState<'details' | 'devices' | 'history'>('details');
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; name: string } | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorCtx, setErrorCtx] = useState<ErrorContext | undefined>(undefined);
  const [editingJobSite, setEditingJobSite] = useState<JobSite | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [aspireFilter, setAspireFilter] = useState<'all' | 'linked' | 'unlinked'>('all');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchJobSites = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiClient.get<JobSite[]>('/api/job-sites');
      setAllJobSites(data);
    } catch (error) {
      console.error('Error fetching job sites:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchJobSites();
  }, [fetchJobSites]);

  const filteredJobSites = useMemo(() => {
    let rows = [...allJobSites];

    const term = debouncedSearch.trim().toLowerCase();
    if (term) {
      rows = rows.filter((r) =>
        r.name.toLowerCase().includes(term)
        || (r.address ?? '').toLowerCase().includes(term),
      );
    }

    if (aspireFilter === 'linked') {
      rows = rows.filter((r) => r.aspire_property_id !== null);
    } else if (aspireFilter === 'unlinked') {
      rows = rows.filter((r) => r.aspire_property_id === null);
    }

    rows.sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      let av: string;
      let bv: string;
      if (sortField === 'name') {
        av = a.name ?? '';
        bv = b.name ?? '';
      } else if (sortField === 'address') {
        av = a.address ?? '';
        bv = b.address ?? '';
      } else {
        av = a.created_at ?? '';
        bv = b.created_at ?? '';
      }
      const cmp = av.localeCompare(bv);
      if (cmp !== 0) return cmp * dir;
      // Secondary sort by name when primary key is non-name
      if (sortField !== 'name') return (a.name ?? '').localeCompare(b.name ?? '');
      return 0;
    });

    return rows;
  }, [allJobSites, debouncedSearch, aspireFilter, sortField, sortDir]);

  const totalCount = filteredJobSites.length;
  const pagedJobSites = useMemo(() => {
    const start = page * pageSize;
    return filteredJobSites.slice(start, start + pageSize);
  }, [filteredJobSites, page, pageSize]);

  const handleSort = (key: string) => {
    const field = key as SortField;
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
    setPage(0);
  };

  const handleSave = async (formData: {
    name: string;
    address: string;
    contact_name: string;
    contact_phone: string;
    contact_email: string;
    gps_location: GPSLocation | null;
    notes: string;
    aspire_property_id: number | null;
  }): Promise<string | void> => {
    if (readOnly) return;
    const payload: Record<string, unknown> = {
      name: formData.name.trim(),
      address: formData.address.trim() || null,
      contact_name: formData.contact_name.trim() || null,
      contact_phone: formData.contact_phone.trim() || null,
      contact_email: formData.contact_email.trim() || null,
      gps_location: formData.gps_location,
      notes: formData.notes.trim() || null,
    };

    if (formData.aspire_property_id !== null) {
      payload.aspire_property_id = formData.aspire_property_id;
    }

    try {
      if (editingJobSite) {
        await apiClient.put(`/api/job-sites/${editingJobSite.id}`, payload);
        logAudit({
          action: AUDIT_ACTIONS.UPDATE,
          entityType: AUDIT_ENTITIES.PROPERTY,
          entityId: editingJobSite.id,
          entityLabel: formData.name.trim(),
          details: computeChanges(
            { name: editingJobSite.name, address: editingJobSite.address, contact_name: editingJobSite.contact_name, contact_phone: editingJobSite.contact_phone, contact_email: editingJobSite.contact_email, notes: editingJobSite.notes, aspire_property_id: editingJobSite.aspire_property_id },
            { name: payload.name, address: payload.address, contact_name: payload.contact_name, contact_phone: payload.contact_phone, contact_email: payload.contact_email, notes: payload.notes, aspire_property_id: payload.aspire_property_id },
          ),
          pageContext: 'properties',
        });
        const savedId = editingJobSite.id;
        setViewMode('list');
        setEditingJobSite(null);
        fetchJobSites();
        return savedId;
      } else {
        const created = await apiClient.post<JobSite>('/api/job-sites', payload);
        logAudit({
          action: AUDIT_ACTIONS.CREATE,
          entityType: AUDIT_ENTITIES.PROPERTY,
          entityId: created.id,
          entityLabel: formData.name.trim(),
          details: { name: payload.name, address: payload.address, contact_name: payload.contact_name, contact_email: payload.contact_email },
          pageContext: 'properties',
        });
        setViewMode('list');
        setEditingJobSite(null);
        fetchJobSites();
        return created.id;
      }
    } catch (error) {
      console.error('Error saving job site:', error);
      setErrorMessage('Failed to save property. Please try again.');
      setErrorCtx({ error, componentName: 'JobSitesManagement', action: 'saveJobSite', pageContext: 'properties' });
      throw error;
    }
  };

  const handleDelete = async () => {
    if (readOnly || !deleteConfirm) return;

    try {
      await apiClient.del(`/api/job-sites/${deleteConfirm.id}`);
      logAudit({
        action: AUDIT_ACTIONS.DELETE,
        entityType: AUDIT_ENTITIES.PROPERTY,
        entityId: deleteConfirm.id,
        entityLabel: deleteConfirm.name,
        pageContext: 'properties',
      });
      setDeleteConfirm(null);
      fetchJobSites();
    } catch (error) {
      console.error('Error deleting job site:', error);
      setDeleteConfirm(null);
      setErrorMessage('Failed to delete property. It may be in use by existing reports.');
      setErrorCtx({ error, componentName: 'JobSitesManagement', action: 'deleteJobSite', pageContext: 'properties' });
    }
  };

  const handleOpenAddModal = () => {
    if (readOnly) return;
    setEditingJobSite(null);
    setDetailTab('details');
    setViewMode('detail');
  };

  const handleOpenEditModal = (site: JobSite) => {
    setEditingJobSite(site);
    setDetailTab('details');
    setViewMode('detail');
  };

  const handleCloseModal = () => {
    setViewMode('list');
    setEditingJobSite(null);
  };

  const handleViewControllers = (site: JobSite) => {
    setEditingJobSite(site);
    setDetailTab('devices');
    setViewMode('detail');
  };

  const handleBackToList = () => {
    setViewMode('list');
    setEditingJobSite(null);
  };

  const columns: ColumnDef<JobSite>[] = useMemo(() => [
    {
      key: 'name',
      label: 'Property Name',
      sortable: true,
      render: (site) => (
        <div className="flex items-center gap-2.5">
          <MapPin size={16} className="text-theme-secondary shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-medium text-theme-primary truncate flex items-center gap-1.5">
              {site.name}
              {site.aspire_property_id && (
                <span title={`Linked to Aspire (ID: ${site.aspire_property_id})`}>
                  <Link2 size={13} className="text-emerald-500 shrink-0" />
                </span>
              )}
            </div>
            <div className="md:hidden text-xs text-theme-secondary truncate mt-0.5">
              {site.address || '--'}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'address',
      label: 'Address',
      sortable: true,
      headerClassName: 'hidden md:table-cell',
      cellClassName: 'hidden md:table-cell',
      render: (site) => (
        <span className="text-sm text-theme-secondary truncate block max-w-xs">
          {site.address || '--'}
        </span>
      ),
    },
    {
      key: 'contact',
      label: 'Contact',
      headerClassName: 'hidden lg:table-cell',
      cellClassName: 'hidden lg:table-cell',
      render: (site) => (
        <span className="text-sm text-theme-secondary truncate block max-w-[180px]">
          {site.contact_name || '--'}
        </span>
      ),
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      headerClassName: 'hidden lg:table-cell',
      cellClassName: 'hidden lg:table-cell',
      render: (site) => (
        <span className="text-sm text-theme-secondary">
          {new Date(site.created_at).toLocaleDateString()}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      headerClassName: 'text-right',
      render: (site) => (
        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => handleViewControllers(site)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-md transition-colors dark:bg-blue-500/10 dark:text-blue-400 dark:hover:bg-blue-500/20"
            title={readOnly ? t('properties.viewDevices', 'View Devices') : 'Manage Devices'}
          >
            <Monitor size={14} />
            <span className="hidden sm:inline">Devices</span>
          </button>
          {!readOnly && (
            <>
              <button
                onClick={() => handleOpenEditModal(site)}
                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-md transition-colors dark:text-blue-400 dark:hover:bg-blue-500/10"
                title="Edit"
              >
                <Edit2 size={16} />
              </button>
              <button
                onClick={() => setDeleteConfirm({ id: site.id, name: site.name })}
                className="p-1.5 text-red-600 hover:bg-red-50 rounded-md transition-colors dark:text-red-400 dark:hover:bg-red-500/10"
                title="Delete"
              >
                <Trash2 size={16} />
              </button>
            </>
          )}
        </div>
      ),
    },
  ], [readOnly, t]);

  if (viewMode === 'detail') {
    // Existing property → full-page detail screen with tabs. Adding a new
    // property has no devices/history yet, so it shows only the Details form.
    const isExisting = editingJobSite != null;
    const tab: 'details' | 'devices' | 'history' = isExisting ? detailTab : 'details';
    const tabBtn = (key: 'details' | 'devices' | 'history', label: string) => (
      <button
        onClick={() => setDetailTab(key)}
        className={`px-4 py-2 font-medium border-b-2 transition-colors ${
          tab === key
            ? 'border-blue-600 text-blue-600'
            : 'border-transparent text-theme-secondary hover:text-theme-primary hover:border-line'
        }`}
      >
        {label}
      </button>
    );
    return (
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <button
            onClick={handleBackToList}
            className="text-blue-600 hover:text-blue-800 mb-4"
          >
            &larr; Back to Properties
          </button>
          {isExisting && (
            <>
              <h1 className="text-2xl font-bold text-theme-primary mb-1">{editingJobSite!.name}</h1>
              <div className="mb-4 border-b border-line">
                <div className="flex gap-4">
                  {tabBtn('details', 'Details')}
                  {tabBtn('devices', 'Devices')}
                  {tabBtn('history', 'Inspection History')}
                </div>
              </div>
            </>
          )}
          {tab === 'details' ? (
            <JobSiteFormModal
              variant="page"
              isOpen
              jobSite={editingJobSite || undefined}
              onClose={handleCloseModal}
              onSave={handleSave}
              readOnly={readOnly}
            />
          ) : tab === 'devices' ? (
            <JobSiteDevices jobSiteId={editingJobSite!.id} jobSiteName={editingJobSite!.name} readOnly={readOnly} />
          ) : (
            <JobSiteInspectionHistory jobSiteId={editingJobSite!.id} jobSiteName={editingJobSite!.name} />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto h-full flex flex-col overflow-hidden">
      <div className="flex items-start justify-between mb-4 flex-shrink-0">
        <div>
          <h1 className="text-2xl font-bold text-theme-primary">
            {readOnly ? t('properties.listTitle', 'Properties') : 'Properties Management'}
          </h1>
          <p className="text-sm text-theme-secondary mt-1">
            {readOnly
              ? t('properties.listSubtitleReadOnly', 'View property details, devices, and inspection history')
              : 'Manage properties, controllers, and zones for inspection reports'}
          </p>
        </div>
        {!readOnly && (
          <button
            onClick={handleOpenAddModal}
            className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
          >
            <Plus size={18} />
            Add Property
          </button>
        )}
      </div>

      <div className="bg-surface rounded-lg shadow-sm border border-line p-3 mb-3 flex-shrink-0">
        <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center justify-between">
          <div className="relative flex-1 max-w-md w-full">
            <Search size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-theme-secondary" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name or address..."
              className="w-full pl-8 pr-9 py-1.5 text-sm border border-line rounded-md bg-surface text-theme-primary focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-theme-secondary hover:text-theme-primary"
              >
                <X size={16} />
              </button>
            )}
          </div>
          <div className="flex items-center gap-3 text-sm text-theme-secondary">
            <div className="flex items-center gap-1.5">
              <Filter size={14} className="text-theme-secondary" />
              <select
                value={aspireFilter}
                onChange={(e) => {
                  setAspireFilter(e.target.value as 'all' | 'linked' | 'unlinked');
                  setPage(0);
                }}
                className="border border-line rounded-md px-2 py-1.5 text-sm bg-surface text-theme-primary focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Properties</option>
                <option value="linked">Linked to Aspire</option>
                <option value="unlinked">Not Linked</option>
              </select>
            </div>
            <span className="whitespace-nowrap text-xs text-theme-secondary">
              {totalCount} {totalCount === 1 ? 'property' : 'properties'}
            </span>
          </div>
        </div>
      </div>

      <DataTable
        data={pagedJobSites}
        columns={columns}
        keyField="id"
        loading={loading}
        emptyIcon={<MapPin size={40} className="text-theme-secondary" />}
        emptyTitle={debouncedSearch ? 'No results found' : 'No properties yet'}
        emptyDescription={
          debouncedSearch
            ? `No properties match "${debouncedSearch}"`
            : readOnly
              ? t('properties.emptyReadOnly', 'No properties have been set up yet')
              : 'Add your first property to get started'
        }
        sortKey={sortField}
        sortDir={sortDir}
        onSort={handleSort}
        serverPagination
        totalCount={totalCount}
        page={page}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={(size) => { setPageSize(size); setPage(0); }}
        onRowClick={handleOpenEditModal}
      />

      <ConfirmModal
        isOpen={deleteConfirm !== null}
        title="Delete Property"
        message={`Are you sure you want to delete "${deleteConfirm?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirm(null)}
      />

      <ConfirmModal
        isOpen={errorMessage !== null}
        title="Error"
        message={errorMessage || ''}
        confirmText="OK"
        variant="danger"
        onConfirm={() => { setErrorMessage(null); setErrorCtx(undefined); }}
        onCancel={() => { setErrorMessage(null); setErrorCtx(undefined); }}
        errorContext={errorCtx}
      />
    </div>
  );
}
